"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { LoadingSpinner } from "@offstreetparking/design-system/feedback";
import { Button } from "@offstreetparking/design-system/forms";
import { UserIcon } from "@offstreetparking/design-system/icons/UserIcon";
import { Modal, ModalTitle } from "@offstreetparking/design-system/overlay";
import { useProductTracking } from "@offstreetparking/design-system/providers";
import { Text } from "@offstreetparking/design-system/typography";
import { usePermitFormData } from "@offstreetparking/portal-flow/components/CreatePermitWizard/PermitContextProvider";
import { useMutation } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { requestPasswordReset } from "~/app/[locale]/forgot-password/actions";
import { ForgotPasswordFormContent } from "~/app/[locale]/forgot-password/components/ForgotPasswordForm";
import { loginWithCompanySSO, loginWithPassword } from "~/app/[locale]/login/actions";
import { LoginFormContent } from "~/app/[locale]/login/components/LoginForm";
import { logout } from "~/app/[locale]/logout/actions";
import { signUpDuringCheckout } from "~/app/[locale]/signup/actions";
import { SignUpFormContent } from "~/app/[locale]/signup/components/SignUpForm";
import { resendVerificationCode, verifyEmailCode } from "~/app/[locale]/verify-email/actions";
import { VerifyEmailFormContent } from "~/app/[locale]/verify-email/components/VerifyEmailForm";
import { useParker } from "~/components/providers/AuthProvider";
import { useCompany } from "~/components/providers/CompanyProvider";
import { type AuthError, handleErrorOnClient } from "~/lib/auth";
import { offstreetClient } from "~/services/offstreetApi";

const AUTH_MODE = {
	SIGN_IN: "SIGNIN",
	SIGN_UP: "SIGNUP",
	VERIFY_EMAIL: "VERIFY_EMAIL",
	FORGOT_PASSWORD: "FORGOT_PASSWORD",
} as const;
type AuthMode = (typeof AUTH_MODE)[keyof typeof AUTH_MODE];

type PendingVerification = {
	pendingAuthenticationToken: string;
	userId: string;
	email: string;
};

export const SignInPrompt = () => {
	const { i18n, t } = useLingui();
	const pathname = usePathname();
	const { capture } = useProductTracking();
	const { refreshAuth, displayName, parker, isLoggedIn } = useParker();
	const { company, isFromSubdomain } = useCompany();
	const utils = offstreetClient.useUtils();
	const [isAuthOpen, setIsAuthOpen] = useState(false);
	const [mode, setMode] = useState<AuthMode>(AUTH_MODE.SIGN_IN);
	const [pendingVerification, setPendingVerification] = useState<PendingVerification>();
	const [isChangeAccountOpen, setIsChangeAccountOpen] = useState(false);
	const checkoutEmail = usePermitFormData().email || undefined;

	const switchPane = (target: "signIn" | "signUp" | "forgotPassword") => {
		setMode(
			target === "signUp"
				? AUTH_MODE.SIGN_UP
				: target === "forgotPassword"
					? AUTH_MODE.FORGOT_PASSWORD
					: AUTH_MODE.SIGN_IN,
		);
	};

	const finishAuth = (action: "signIn" | "signUp") => {
		capture({ feature: "optional_parker_accounts", action, object: "form", context: "permit_form" }, {});
		closeAuthModal();
		void Promise.allSettled([refreshAuth()]);
	};

	const {
		mutate: verifyCode,
		isPending: isVerifying,
		error: verificationError,
		reset: resetVerification,
	} = useMutation<void, AuthError, { code: string }>({
		mutationFn: async ({ code }) => {
			if (!pendingVerification) {
				return;
			}
			await handleErrorOnClient(
				verifyEmailCode({
					code,
					pendingAuthenticationToken: pendingVerification.pendingAuthenticationToken,
					subdomain: isFromSubdomain ? (company?.subdomain ?? undefined) : undefined,
					locale: i18n.locale,
				}),
			);
			finishAuth("signUp");
		},
	});

	const { mutate: signOut, isPending: isSigningOut } = useMutation({
		mutationFn: async () => {
			await Promise.allSettled([logout()]);
			await Promise.allSettled([refreshAuth()]);
			await utils.invalidate();
		},
		onSuccess: () => {
			capture(
				{ feature: "optional_parker_accounts", action: "sign_out", object: "button", context: "permit_form" },
				{},
			);
			setIsChangeAccountOpen(false);
		},
	});

	const closeAuthModal = () => {
		resetVerification();
		setIsAuthOpen(false);
		setMode(AUTH_MODE.SIGN_IN);
		setPendingVerification(undefined);
	};

	return (
		<>
			<div
				className={`flex max-w-full items-center gap-3 border-b ${isLoggedIn ? "border-success bg-success-faded" : "border-neutral-faded bg-neutral-faded"} px-4 py-2`}
			>
				<AccountBar
					isLoggedIn={isLoggedIn}
					onOpenSignIn={() => {
						capture(
							{
								feature: "optional_parker_accounts",
								action: "open",
								object: "button",
								context: "permit_form",
							},
							{},
						);
						setIsAuthOpen(true);
					}}
					onOpenChangeAccount={() => setIsChangeAccountOpen(true)}
				/>
			</div>

			<Modal
				i18n={{ closeButton: t`Close`, cancelButton: t`Cancel` }}
				isOpen={isAuthOpen}
				onClose={closeAuthModal}
				variant="fullScreen"
			>
				{mode === AUTH_MODE.VERIFY_EMAIL && pendingVerification ? (
					<VerifyEmailPane
						pendingVerification={pendingVerification}
						isVerifying={isVerifying}
						verificationError={verificationError}
						onVerifyCode={(code) => verifyCode({ code })}
						onResetVerification={resetVerification}
					/>
				) : mode === AUTH_MODE.SIGN_UP ? (
					<SignUpPane
						email={checkoutEmail}
						redirectTo={pathname}
						onSwitchTo={switchPane}
						onVerificationRequired={(pending) => {
							setPendingVerification(pending);
							setMode(AUTH_MODE.VERIFY_EMAIL);
						}}
						onSignedUp={() => finishAuth("signUp")}
						onClose={closeAuthModal}
					/>
				) : mode === AUTH_MODE.FORGOT_PASSWORD ? (
					<ForgotPasswordPane email={checkoutEmail} onSwitchTo={switchPane} />
				) : (
					<SignInPane
						email={checkoutEmail}
						redirectTo={pathname}
						onSwitchTo={switchPane}
						onSignedIn={() => finishAuth("signIn")}
						onClose={closeAuthModal}
					/>
				)}
			</Modal>

			<Modal
				i18n={{ closeButton: t`Close`, cancelButton: t`Cancel` }}
				isOpen={isChangeAccountOpen}
				onClose={() => setIsChangeAccountOpen(false)}
				variant="fullScreen"
			>
				<ModalTitle>
					{t({ message: "Change account", comment: "Title of the change-account dialog during checkout" })}
				</ModalTitle>

				<div className="space-y-4">
					<div>
						<Text weight="bold">{displayName}</Text>
						{parker?.email && (
							<Text size="sm" intent="muted">
								{parker.email}
							</Text>
						)}
					</div>

					<Text size="sm" intent="muted">
						{t`Sign out to change accounts or continue as a guest.`}
					</Text>

					<Button
						intent="secondary"
						type="button"
						className="w-full justify-center"
						isDisabled={isSigningOut}
						onClick={() => signOut()}
					>
						{t({ message: "Sign out", comment: "Button that signs the parker out during checkout" })}
					</Button>
				</div>
			</Modal>
		</>
	);
};

const AccountBar = ({
	onOpenSignIn,
	onOpenChangeAccount,
	isLoggedIn,
}: { onOpenSignIn: () => void; onOpenChangeAccount: () => void; isLoggedIn: boolean }) => {
	const { t } = useLingui();
	const { displayName, needsParkerCreation } = useParker();

	if (isLoggedIn && needsParkerCreation) {
		return (
			<div className="flex flex-1 items-center gap-2">
				<LoadingSpinner size="sm" />
				<Text size="sm" weight="bold">
					{t({
						message: "Setting up your account",
						comment: "Title shown while waiting for account setup to finish after sign-up",
					})}
				</Text>
			</div>
		);
	}

	if (isLoggedIn) {
		return (
			<>
				<div className="flex flex-1 items-center gap-1">
					<UserIcon className="h-4 min-h-4 w-4 min-w-4 text-success" aria-hidden="true" />
					<Text size="sm" weight="bold">{t`Parking as ${displayName}`}</Text>
				</div>
				<Button intent="action" type="button" onClick={onOpenChangeAccount}>
					{t({
						message: "Change",
						comment: "Opens the change-account dialog during permit checkout",
					})}
				</Button>
			</>
		);
	}

	return (
		<>
			<div className="flex-1">
				<Text size="sm" weight="bold">{t`Parking as a guest`}</Text>
				<Text size="xs" intent="muted">{t`Sign in for faster checkout`}</Text>
			</div>
			<Button intent="action" type="button" onClick={onOpenSignIn}>
				{t({
					message: "Sign in",
					comment: "Link that opens the sign-in dialog during permit checkout",
				})}
			</Button>
		</>
	);
};

const VerifyEmailPane = ({
	pendingVerification,
	isVerifying,
	verificationError,
	onVerifyCode,
	onResetVerification,
}: {
	pendingVerification: PendingVerification;
	isVerifying: boolean;
	verificationError: Error | null;
	onVerifyCode: (code: string) => void;
	onResetVerification: () => void;
}) => {
	const { i18n, t } = useLingui();
	const email = pendingVerification.email;
	return (
		<>
			<ModalTitle>{t`Verify your email`}</ModalTitle>
			<div className="space-y-4">
				<Text size="sm" intent="muted">
					<Trans>
						Enter the verification code sent to <b>{email}</b>. It may take a few minutes for the email to
						arrive.
					</Trans>
				</Text>
				<VerifyEmailFormContent
					token={pendingVerification.pendingAuthenticationToken}
					isVerifying={isVerifying}
					verificationError={verificationError}
					onVerifyCode={({ code }) => onVerifyCode(code)}
					onResetVerification={onResetVerification}
					onResendCode={async () => {
						await handleErrorOnClient(
							resendVerificationCode({ userId: pendingVerification.userId, locale: i18n.locale }),
						);
					}}
				/>
			</div>
		</>
	);
};

const SignUpPane = ({
	email,
	redirectTo,
	onSwitchTo,
	onVerificationRequired,
	onSignedUp,
	onClose,
}: {
	email?: string;
	redirectTo: string;
	onSwitchTo: (target: "signIn") => void;
	onVerificationRequired: (pending: PendingVerification) => void;
	onSignedUp: () => void;
	onClose: () => void;
}) => {
	const { i18n, t } = useLingui();
	return (
		<>
			<ModalTitle variant="fullScreen" onClose={onClose} i18n={{ closeButton: t`Close` }}>
				{t({
					message: "Create account",
					comment: "Title of the sign-up dialog during permit checkout",
				})}
			</ModalTitle>
			<div className="space-y-4">
				<Text size="sm" intent="muted">
					{t`Create an account to save this permit and your vehicles, then finish checking out.`}
				</Text>
				<SignUpFormContent
					email={email}
					redirectTo={redirectTo}
					onSwitchTo={onSwitchTo}
					onProviderSignedIn={onSignedUp}
					onSignUp={async (values) => {
						const result = await handleErrorOnClient(
							signUpDuringCheckout({
								firstName: values.firstName,
								lastName: values.lastName,
								email: values.email,
								password: values.password,
								companyId: values.companyId,
								subdomain: values.subdomain,
								locale: i18n.locale,
							}),
						);
						if (result?.status === "verification_required") {
							onVerificationRequired({
								pendingAuthenticationToken: result.pendingAuthenticationToken,
								userId: result.userId,
								email: values.email,
							});
							return;
						}
						onSignedUp();
					}}
				/>
			</div>
		</>
	);
};

const SignInPane = ({
	email,
	redirectTo,
	onSwitchTo,
	onSignedIn,
	onClose,
}: {
	email?: string;
	redirectTo: string;
	onSwitchTo: (target: "signUp" | "forgotPassword") => void;
	onSignedIn: () => void;
	onClose: () => void;
}) => {
	const { i18n, t } = useLingui();
	return (
		<>
			<ModalTitle variant="fullScreen" onClose={onClose} i18n={{ closeButton: t`Close` }}>
				{t({ message: "Sign in", comment: "Title of the sign-in dialog during permit checkout" })}
			</ModalTitle>
			<div className="space-y-4">
				<Text size="sm" intent="muted">
					{t`Sign in to manage your permits, access saved vehicles, and check out faster.`}
				</Text>
				<LoginFormContent
					redirectTo={redirectTo}
					email={email}
					onSwitchTo={onSwitchTo}
					onProviderSignedIn={onSignedIn}
					onRedirectToSSO={(values) => loginWithCompanySSO({ ...values, redirectTo })}
					onLoginWithPassword={async (values) => {
						await handleErrorOnClient(loginWithPassword({ ...values, redirectTo, locale: i18n.locale }));
						onSignedIn();
					}}
				/>
			</div>
		</>
	);
};

const ForgotPasswordPane = ({
	email,
	onSwitchTo,
}: {
	email?: string;
	onSwitchTo: (target: "signIn") => void;
}) => {
	const { i18n, t } = useLingui();
	return (
		<>
			<ModalTitle>{t`Reset Your Password`}</ModalTitle>
			<div className="space-y-4">
				<Text size="sm" intent="muted">
					{t`Enter your email to receive password reset instructions`}
				</Text>
				<ForgotPasswordFormContent
					onRequestReset={(data) => requestPasswordReset({ ...data, locale: i18n.locale })}
					email={email}
					onSwitchTo={onSwitchTo}
				/>
			</div>
		</>
	);
};
